import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { database } from '../firebaseConfig';
import { ref as dbRef, set } from 'firebase/database';
import { v4 as uuidv4 } from 'uuid';
import { hasPermission, Permissions } from '../services/authService';
import {
    Container,
    Typography,
    TextField,
    Button,
    Box,
    Alert,
    Grid,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    IconButton,
    Paper
} from '@mui/material';
import { Delete as DeleteIcon, Image as ImageIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import '../styles/Upload.css';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebaseConfig';

const Upload = ({ user, userRole }) => {
    const navigate = useNavigate();
    const [albumTitle, setAlbumTitle] = useState('');
    const [albumSummary, setAlbumSummary] = useState('');
    const [photos, setPhotos] = useState([]);
    const [coverPhotoIndex, setCoverPhotoIndex] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);

    if (!hasPermission(userRole, Permissions.CREATE_ALBUM)) {
        return (
            <Container maxWidth="sm" sx={{ mt: 10 }}>
                <Alert severity="error">
                    You don't have permission to upload photos.
                </Alert>
            </Container>
        );
    }

    const onDrop = useCallback((acceptedFiles) => {
        const newPhotos = acceptedFiles.map(file => ({
            id: uuidv4(),
            file,
            title: '',
            description: '',
            previewUrl: URL.createObjectURL(file)
        }));
        setPhotos(prev => [...prev, ...newPhotos]);

        if (coverPhotoIndex === null && photos.length === 0) {
            setCoverPhotoIndex(0);
        }
    }, [photos.length, coverPhotoIndex]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

    const uploadPhotoWithProgress = async (photo, albumId) => {
        const photoId = uuidv4();
        const storageRef = ref(storage, `gallery/legacy/photos/${photoId}`);
        const uploadTask = uploadBytesResumable(storageRef, photo.file);

        return new Promise((resolve, reject) => {
            uploadTask.on('state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    console.log('Upload is ' + progress + '% done');
                },
                (error) => {
                    console.error('Upload error:', error);
                    reject(error);
                },
                async () => {
                    try {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        resolve({ photoId, url: downloadURL });
                    } catch (error) {
                        console.error('Error getting download URL:', error);
                        reject(error);
                    }
                }
            );
        });
    };

    const handleUpload = async () => {
        if (!albumTitle.trim()) {
            setError('Album title is required');
            return;
        }

        if (photos.length === 0) {
            setError('Please add at least one photo');
            return;
        }

        if (coverPhotoIndex === null) {
            setError('Please select a cover photo');
            return;
        }

        try {
            setUploading(true);
            setError(null);

            const albumId = uuidv4();
            const timestamp = Math.floor(Date.now() / 1000);

            const coverPhoto = photos[coverPhotoIndex];
            const { photoId: coverPhotoId, url: coverPhotoUrl } = await uploadPhotoWithProgress(coverPhoto, albumId);

            const coverPhotoData = {
                title: coverPhoto.title || `Cover Photo ${timestamp}`,
                description: coverPhoto.description || `Uploaded on ${new Date(timestamp * 1000).toLocaleString()}`,
                creation_date: timestamp,
                url: coverPhotoUrl,
                legacy_path_component: coverPhoto.file.name,
                parent_album: albumId,
                summary: coverPhoto.description || "No description provided",
                legacy_id: 0,
                legacy_owner_id: user.uid,
                legacy_parent_id: 0,
            };

            const coverPhotoRef = dbRef(database, `gallery/legacy/photos/${coverPhotoId}`);
            await set(coverPhotoRef, coverPhotoData);

            const albumData = {
                title: albumTitle,
                summary: albumSummary || 'No description provided',
                creation_date: timestamp,
                cover_image: coverPhotoId,
                legacy_id: 0,
                legacy_owner_id: user.uid,
                legacy_parent_id: 0,
                parent_album: 0
            };

            const albumRef = dbRef(database, `gallery/legacy/albums/${albumId}`);
            await set(albumRef, albumData);

            for (let i = 0; i < photos.length; i++) {
                if (i === coverPhotoIndex) continue;
                const photo = photos[i];
                const { photoId, url } = await uploadPhotoWithProgress(photo, albumId);

                const photoData = {
                    title: photo.title || `Photo ${timestamp}`,
                    description: photo.description || `Uploaded on ${new Date(timestamp * 1000).toLocaleString()}`,
                    creation_date: timestamp,
                    url: url,
                    legacy_path_component: photo.file.name,
                    parent_album: albumId,
                    summary: photo.description || "No description provided",
                    legacy_id: 0,
                    legacy_owner_id: user.uid,
                    legacy_parent_id: 0,
                };

                const photoRef = dbRef(database, `gallery/legacy/photos/${photoId}`);
                await set(photoRef, photoData);
            }

            navigate(`/album/${albumId}`);
        } catch (error) {
            console.error('Upload error:', error);
            setError('Failed to upload photos. Please try again.');
            setUploading(false);
        }
    };

    return (
        <Container className="upload-container">
            <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
                    Create New Album
                </Typography>
                
                <Box className="album-details">
                    <TextField
                        label="Album Title"
                        value={albumTitle}
                        onChange={(e) => setAlbumTitle(e.target.value)}
                        fullWidth
                        variant="outlined"
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Album Summary"
                        value={albumSummary}
                        onChange={(e) => setAlbumSummary(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                    />
                </Box>

                <Paper 
                    {...getRootProps()} 
                    className={`dropzone ${isDragActive ? 'active' : ''}`}
                    variant="outlined"
                    sx={{ 
                        mt: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        p: 4
                    }}
                >
                    <input {...getInputProps()} />
                    <CloudUploadIcon sx={{ fontSize: 48, mb: 2, color: 'primary.main' }} />
                    <Typography variant="h6" gutterBottom>
                        {isDragActive ? 'Drop photos here' : 'Drag & drop photos here'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        or click to select files
                    </Typography>
                </Paper>

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}

                {photos.length > 0 && (
                    <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" gutterBottom>
                            Selected Photos ({photos.length})
                        </Typography>
                        <Grid container spacing={2} className="photos-grid">
                            {photos.map((photo, index) => (
                                <Grid item xs={12} sm={6} md={4} key={photo.id}>
                                    <Card className="photo-item">
                                        <CardMedia
                                            component="img"
                                            height="200"
                                            image={photo.previewUrl}
                                            alt={photo.title}
                                        />
                                        <CardContent>
                                            <TextField
                                                placeholder="Photo title"
                                                value={photo.title}
                                                onChange={(e) => {
                                                    const updatedPhotos = [...photos];
                                                    updatedPhotos[index].title = e.target.value;
                                                    setPhotos(updatedPhotos);
                                                }}
                                                fullWidth
                                                variant="standard"
                                                size="small"
                                            />
                                        </CardContent>
                                        <CardActions className="photo-actions">
                                            <IconButton 
                                                onClick={() => setCoverPhotoIndex(index)}
                                                color={coverPhotoIndex === index ? 'primary' : 'default'}
                                                title="Set as cover photo"
                                            >
                                                <ImageIcon />
                                            </IconButton>
                                            <IconButton 
                                                onClick={() => setPhotos(photos.filter((_, i) => i !== index))}
                                                color="error"
                                                title="Remove photo"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpload}
                    disabled={uploading}
                    fullWidth
                    size="large"
                    sx={{ mt: 4 }}
                    className="upload-button"
                >
                    {uploading ? 'Uploading...' : 'Create Album'}
                </Button>
            </Paper>
        </Container>
    );
};

export default Upload;
